import { useCallback, useEffect, useMemo, useState } from "react";
import StatusBadge from "../intelligence/StatusBadge";
import { getAdminCanonicalPropertyHistory } from "../../../services/midpointApi";
import { formatPropertyPrice } from "../../../utils/realEstateFormat";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "price", label: "Price" },
  { id: "availability", label: "Availability" },
  { id: "source", label: "Source listing" },
  { id: "canonical", label: "Canonical" },
  { id: "admin", label: "Admin" },
];

function matchesFilter(event, filter) {
  if (filter === "all") {
    return true;
  }

  const type = event.changeType || "";

  if (filter === "price") {
    return type.includes("PRICE");
  }

  if (filter === "availability") {
    return type.includes("AVAILABILITY") || type.includes("REACTIVATED") || type.includes("DEACTIVATED");
  }

  if (filter === "source") {
    return Boolean(event.sourceListingId || event.sourceKey);
  }

  if (filter === "canonical") {
    return Boolean(event.canonicalPropertyId) && !event.sourceListingId;
  }

  if (filter === "admin") {
    return type.includes("ADMIN");
  }

  return true;
}

function renderValue(value, currency) {
  if (value == null || value === "") {
    return "—";
  }

  if (typeof value === "number" && currency) {
    return formatPropertyPrice(value, currency);
  }

  return String(value);
}

const CanonicalPropertyHistorySection = ({ canonicalPropertyId }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const loadHistory = useCallback(async () => {
    if (!canonicalPropertyId) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await getAdminCanonicalPropertyHistory(canonicalPropertyId, {
        limit: 120,
      });
      setHistory(response);
    } catch (err) {
      setHistory(null);
      setError(err.message || "Unable to load history.");
    } finally {
      setLoading(false);
    }
  }, [canonicalPropertyId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const timeline = useMemo(() => {
    const events = history?.timeline || [];

    return events.filter((event) => matchesFilter(event, filter));
  }, [history, filter]);

  if (loading) {
    return <p className="intel-meta">Loading history…</p>;
  }

  if (error) {
    return <p className="admin-error">{error}</p>;
  }

  if (!history) {
    return null;
  }

  return (
    <section className="admin-canonical-history">
      <div className="section-heading">
        <h3>History</h3>
        <span className="status-pill">{timeline.length} events</span>
      </div>

      <div className="admin-canonical-history__filters">
        {FILTERS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={filter === entry.id ? "is-active" : ""}
            onClick={() => setFilter(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <ul className="admin-canonical-history__timeline">
        {timeline.map((event) => {
          const currency = event.newCurrency || event.previousCurrency || "COP";

          return (
            <li key={event.id} className="admin-canonical-history__item">
              <div className="admin-canonical-history__item-header">
                <StatusBadge label={event.changeType} tone="neutral" />
                <span className="muted">{new Date(event.changedAt).toLocaleString()}</span>
              </div>
              {event.sourceKey ? <p className="muted">Source: {event.sourceKey}</p> : null}
              {event.changeReason ? <p className="muted">{event.changeReason}</p> : null}
              {event.previousPrice != null || event.newPrice != null ? (
                <p>
                  Price: {renderValue(event.previousPrice, currency)} →{" "}
                  {renderValue(event.newPrice, currency)}
                </p>
              ) : null}
              {event.previousPriceMin != null || event.newPriceMin != null ? (
                <p>
                  Range: {renderValue(event.previousPriceMin, currency)} →{" "}
                  {renderValue(event.newPriceMin, currency)}
                </p>
              ) : null}
              {event.previousStatus || event.newStatus ? (
                <p>
                  Status: {event.previousStatus || "—"} → {event.newStatus || "—"}
                </p>
              ) : null}
              {event.pipelineRunId ? (
                <p className="muted">Run: {event.pipelineRunId}</p>
              ) : null}
            </li>
          );
        })}
      </ul>

      {!timeline.length ? <p className="intel-meta">No history events for this filter.</p> : null}
    </section>
  );
};

export default CanonicalPropertyHistorySection;
